import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, AlertTriangle, Smartphone, Wifi } from "lucide-react";

const RedeemModal = ({ open, onClose, onSubmit, maxPoints }) => {
  const [points, setPoints] = useState("");
  const [conversionType, setConversionType] = useState("data");
  const [errors, setErrors] = useState({});

  // Conversion rates (example rates - adjust as needed)
  const conversionRates = {
    data: {
      rate: 1000, // 1000 points = 1GB
      unit: "GB",
      options: [
        { points: 1000, value: "1GB", label: "1GB Data Bundle" },
        { points: 2000, value: "2GB", label: "2GB Data Bundle" },
        { points: 3000, value: "3GB", label: "3GB Data Bundle" },
        { points: 5000, value: "5GB", label: "5GB Data Bundle" },
        { points: 10000, value: "10GB", label: "10GB Data Bundle" },
      ]
    },
    airtime: {
      rate: 100, // 100 points = 1 GHS
      unit: "GHS",
      options: [
        { points: 500, value: "5", label: "GHS 5 Airtime" },
        { points: 1000, value: "10", label: "GHS 10 Airtime" },
        { points: 2000, value: "20", label: "GHS 20 Airtime" },
        { points: 5000, value: "50", label: "GHS 50 Airtime" },
        { points: 10000, value: "100", label: "GHS 100 Airtime" },
      ]
    }
  };

  const validatePoints = (pointsValue) => {
    const numPoints = parseInt(pointsValue);
    const errors = {};

    if (!pointsValue) {
      errors.points = "Please enter points to redeem";
    } else if (isNaN(numPoints) || numPoints <= 0) {
      errors.points = "Please enter a valid number";
    } else if (numPoints > maxPoints) {
      errors.points = `Cannot exceed maximum available points (${maxPoints.toLocaleString()})`;
    } else if (numPoints < getMinimumPoints()) {
      errors.points = `Minimum points required: ${getMinimumPoints().toLocaleString()}`;
    }

    return errors;
  };

  const getMinimumPoints = () => {
    const currentRate = conversionRates[conversionType];
    return currentRate.options[0].points;
  };

  const getConversionValue = () => {
    if (!points || isNaN(parseInt(points))) return null;
    
    const numPoints = parseInt(points);
    const currentRate = conversionRates[conversionType];
    
    if (conversionType === "data") {
      return (numPoints / currentRate.rate).toFixed(1) + " GB";
    } else {
      return "GHS " + (numPoints / currentRate.rate).toFixed(0);
    }
  };

  const handlePointsChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setPoints(value);
    setErrors(prev => ({ ...prev, points: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validatePoints(points);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const conversionValue = getConversionValue();
    
    onSubmit({
      points: parseInt(points),
      conversionType,
      conversionValue,
      conversionTypeLabel: conversionType === "data" ? "Data Bundle" : "Airtime"
    });
    
    // Reset form
    setPoints("");
    setConversionType("data");
    setErrors({});
  };

  const handleClose = () => {
    setPoints("");
    setConversionType("data");
    setErrors({});
    onClose();
  };

  const getSuggestedOptions = () => {
    return conversionRates[conversionType].options.filter(option => option.points <= maxPoints);
  };

  const handleSuggestedOption = (option) => {
    setPoints(option.points.toString());
    setErrors(prev => ({ ...prev, points: null }));
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-4xl bg-white rounded-2xl ring-1 ring-gray-100 shadow-2xl max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />

            {/* Fixed Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Redeem Points</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Available: <span className="font-semibold text-emerald-600">{maxPoints.toLocaleString()} points</span>
                </p>
              </div>
              <button
                onClick={handleClose}
                className="grid place-items-center w-10 h-10 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <form id="redeem-form" onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Conversion Type Selection */}
                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      What would you like to convert your points to?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setConversionType("data")}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          conversionType === "data"
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Wifi className="mx-auto mb-2 w-6 h-6" />
                        <span className="text-sm font-medium">Data Bundle</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConversionType("airtime")}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          conversionType === "airtime"
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Smartphone className="mx-auto mb-2 w-6 h-6" />
                        <span className="text-sm font-medium">Airtime</span>
                      </button>
                    </div>
                  </div>

                  {/* Points Input */}
                  <div>
                    <label htmlFor="points" className="block mb-2 text-sm font-medium text-gray-700">
                      Points to Redeem
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="points"
                        value={points}
                        onChange={handlePointsChange}
                        placeholder="Enter points amount"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
                          errors.points ? "border-red-300 bg-red-50" : "border-gray-300"
                        }`}
                      />
                      {points && !errors.points && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <span className="text-sm font-medium text-emerald-600">
                            ≈ {getConversionValue()}
                          </span>
                        </div>
                      )}
                    </div>
                    {errors.points && (
                      <div className="flex items-center mt-2 text-red-600">
                        <AlertTriangle size={16} className="mr-2" />
                        <span className="text-sm">{errors.points}</span>
                      </div>
                    )}
                  </div>

                  {/* Conversion Rate Info */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Conversion Rate:</strong> {conversionRates[conversionType].rate.toLocaleString()} points = 1 {conversionRates[conversionType].unit}
                    </p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Quick Options */}
                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Quick Options
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {getSuggestedOptions().slice(0, 6).map((option) => (
                        <button
                          key={option.points}
                          type="button"
                          onClick={() => handleSuggestedOption(option)}
                          className="flex flex-col items-center p-3 text-center rounded-lg border border-gray-200 transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                        >
                          <span className="text-sm font-medium text-gray-900 mb-1">{option.label}</span>
                          <span className="text-xs text-gray-500">{option.points.toLocaleString()} pts</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Popular Choices Info */}
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <h4 className="text-sm font-medium text-emerald-800 mb-2">💡 Popular Choices</h4>
                    <ul className="text-xs text-emerald-700 space-y-1">
                      <li>• {conversionType === 'data' ? '5GB' : 'GHS 50'} - Most popular for {conversionType === 'data' ? 'heavy users' : 'daily needs'}</li>
                      <li>• {conversionType === 'data' ? '2GB' : 'GHS 20'} - Perfect for {conversionType === 'data' ? 'moderate usage' : 'emergency top-ups'}</li>
                      <li>• {conversionType === 'data' ? '1GB' : 'GHS 10'} - Great for {conversionType === 'data' ? 'light browsing' : 'quick calls'}</li>
                    </ul>
                  </div>
                </div>
              </div>
              </form>
            </div>

            {/* Fixed Actions at Bottom */}
            <div className="flex gap-3 p-6 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-6 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="redeem-form"
                disabled={!points || !!errors.points}
                className="flex-1 px-6 py-3 font-medium text-white bg-emerald-500 rounded-lg transition-colors hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RedeemModal;
